import { useEffect, useState } from 'preact/hooks';
import type { LeafDto } from '../api/types';
import { api, ApiError } from '../api/client';
import { SHAPE_LABEL, historyPath, stepPath, toSeries, type LeafHistoryResponse, type SeriesPoint } from '../api/history';

/**
 * Inline history chart for one leaf. Shown when the operator clicks the
 * mini-chart toggle to the left of the row. Fetches the last hour by
 * default from <c>GET /api/leaves/{id}/history</c> as shape: the M4
 * polyline of all raw points at the chart width, drawn as a step line with
 * gaps where no value was valid — pixel-identical to the raw data, without
 * a charting library.
 */

interface Props {
  leafId: string;
  leaf: LeafDto;
  /** Fires when the user clicks the close (X) — bubbled to the LeafRow so
   *  the row's local expanded state flips off. */
  onClose(): void;
}

interface ChartData {
  from: number;
  to: number;
  series: SeriesPoint[];
  holdAssumed: boolean;
}

/** viewBox width of the SVG; also the shape resolution requested. */
const CHART_WIDTH = 900;

type WindowKey = '15m' | '1h' | '6h' | '24h';
const WINDOWS: { key: WindowKey; label: string; ms: number }[] = [
  { key: '15m', label: '15 Min', ms: 15 * 60_000 },
  { key: '1h', label: '1 h', ms: 60 * 60_000 },
  { key: '6h', label: '6 h', ms: 6 * 60 * 60_000 },
  { key: '24h', label: '24 h', ms: 24 * 60 * 60_000 },
];

export function LeafHistoryChart({ leafId, leaf, onClose }: Props) {
  const [windowKey, setWindowKey] = useState<WindowKey>('1h');
  const [state, setState] = useState<
    | { status: 'loading' }
    | { status: 'ok'; data: ChartData }
    | { status: 'empty' }
    | { status: 'error'; message: string }
  >({ status: 'loading' });

  useEffect(() => {
    const win = WINDOWS.find((w) => w.key === windowKey)!;
    const to = new Date();
    const from = new Date(to.getTime() - win.ms);
    const controller = new AbortController();
    setState({ status: 'loading' });
    (async () => {
      try {
        const res = await api.get<LeafHistoryResponse>(
          historyPath(leafId, from, to, { mode: 'shape', widthPx: CHART_WIDTH }),
          controller.signal,
        );
        if (controller.signal.aborted) return;
        const series = toSeries(res);
        setState(series.every(([, v]) => v === null)
          ? { status: 'empty' }
          : {
            status: 'ok',
            data: {
              from: Date.parse(res.from),
              to: Date.parse(res.to),
              series,
              holdAssumed: res.holdSource === 'assumed',
            },
          });
      } catch (err) {
        if (controller.signal.aborted) return;
        if (err instanceof ApiError && err.status === 404) {
          setState({ status: 'empty' });
        } else {
          setState({ status: 'error', message: 'Verlauf konnte nicht geladen werden.' });
        }
      }
    })();
    return () => controller.abort();
  }, [leafId, windowKey]);

  return (
    <div class="hv-leaf-chart" role="region" aria-label="Verlauf">
      <div class="hv-leaf-chart__header">
        <span class="hv-leaf-chart__title">
          Verlauf — <span class="mono">{leaf.symbol}</span>
          {leaf.displayName ? <span class="secondary"> · {leaf.displayName}</span> : null}
        </span>
        <div class="hv-leaf-chart__windows">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              class={`hv-leaf-chart__win${w.key === windowKey ? ' hv-leaf-chart__win--active' : ''}`}
              onClick={() => setWindowKey(w.key)}
            >
              {w.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          class="hv-leaf-chart__close"
          onClick={onClose}
          aria-label="Verlauf schließen"
          title="Schließen"
        >
          ×
        </button>
      </div>
      {state.status === 'ok' && (
        <div class="hv-leaf-chart__legend">
          <span class="hv-leaf-chart__legend-agg">{SHAPE_LABEL}</span>
          {state.data.holdAssumed && (
            <>
              <span class="hv-leaf-chart__legend-sep">·</span>
              <span
                class="hv-leaf-chart__legend-bucket"
                title="Ohne Heartbeat ist nicht bekannt, wie lange ein Wert gilt. Angenommen sind 24 h."
              >
                Haltedauer angenommen
              </span>
            </>
          )}
        </div>
      )}
      <div class="hv-leaf-chart__body">
        {state.status === 'loading' && <div class="hv-leaf-chart__msg muted">Lade…</div>}
        {state.status === 'empty' && <div class="hv-leaf-chart__msg muted">Keine Werte im Zeitraum.</div>}
        {state.status === 'error' && <div class="hv-leaf-chart__msg hv-leaf-chart__msg--err">{state.message}</div>}
        {state.status === 'ok' && <ChartCanvas data={state.data} leaf={leaf} />}
      </div>
    </div>
  );
}

/** SVG line chart body. Renders axes + optional warn-band overlays + the
 *  step line of the shape. Kept as its own component so it re-renders only
 *  when data changes, not when the window buttons flip. */
function ChartCanvas({ data, leaf }: { data: ChartData; leaf: LeafDto }) {
  const width = CHART_WIDTH;
  const height = 180;
  const padL = 42;
  const padR = 12;
  const padT = 8;
  const padB = 22;
  const plotW = width - padL - padR;
  const plotH = height - padT - padB;

  const points = data.series;
  const tMin = data.from;
  const tMax = data.to;
  const tSpan = Math.max(1, tMax - tMin);

  // Y range: prefer the leaf's declared bounds when both are set — it makes
  // multiple charts of the same leaf comparable across sessions. Fall back
  // to observed min..max with a small padding so the line doesn't hug the
  // frame.
  let yLo: number;
  let yHi: number;
  if (leaf.minValue != null && leaf.maxValue != null && leaf.maxValue > leaf.minValue) {
    yLo = leaf.minValue;
    yHi = leaf.maxValue;
  } else {
    let mn = Infinity;
    let mx = -Infinity;
    for (const [, v] of points) {
      if (v === null) continue;
      if (v < mn) mn = v;
      if (v > mx) mx = v;
    }
    if (mx - mn < 1e-9) { mn -= 1; mx += 1; }
    const pad = (mx - mn) * 0.08;
    yLo = mn - pad;
    yHi = mx + pad;
  }
  const ySpan = yHi - yLo;

  const xFor = (ms: number) => padL + ((ms - tMin) / tSpan) * plotW;
  const yFor = (v: number) => padT + (1 - (v - yLo) / ySpan) * plotH;
  const d = stepPath(points, xFor, yFor);

  const warnBandTop = leaf.warnHigh != null ? yFor(leaf.warnHigh) : null;
  const warnBandBottom = leaf.warnLow != null ? yFor(leaf.warnLow) : null;

  const yTicks = niceTicks(yLo, yHi, 4);
  const xTicks = niceTimeTicks(tMin, tMax, 5);

  const isNumericLike = leaf.valueType === 'Float' || leaf.valueType === 'Int' || leaf.valueType === 'Integer' || leaf.valueType === 'Number';
  const decimals = leaf.decimals ?? (Math.abs(yHi) >= 100 ? 0 : 1);

  return (
    <svg
      class="hv-leaf-chart__svg"
      width="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      role="img"
      aria-label={`Verlauf ${leaf.symbol}`}
    >
      {/* Warn-band overlay: shaded region between WarnHigh and top / between
       *  WarnLow and bottom. Green safe area is implicit. */}
      {warnBandTop != null && (
        <rect
          x={padL}
          y={padT}
          width={plotW}
          height={Math.max(0, warnBandTop - padT)}
          class="hv-leaf-chart__warn-band"
        />
      )}
      {warnBandBottom != null && (
        <rect
          x={padL}
          y={warnBandBottom}
          width={plotW}
          height={Math.max(0, padT + plotH - warnBandBottom)}
          class="hv-leaf-chart__warn-band"
        />
      )}

      {/* Y gridlines + labels */}
      {yTicks.map((t) => (
        <g key={`y-${t}`}>
          <line
            x1={padL}
            y1={yFor(t)}
            x2={padL + plotW}
            y2={yFor(t)}
            class="hv-leaf-chart__gridline"
          />
          <text x={padL - 6} y={yFor(t) + 4} class="hv-leaf-chart__axis-label" text-anchor="end">
            {isNumericLike ? t.toFixed(decimals) : String(t)}
          </text>
        </g>
      ))}

      {/* X gridlines + labels */}
      {xTicks.map((t) => (
        <g key={`x-${t.ms}`}>
          <line
            x1={padL + ((t.ms - tMin) / tSpan) * plotW}
            y1={padT}
            x2={padL + ((t.ms - tMin) / tSpan) * plotW}
            y2={padT + plotH}
            class="hv-leaf-chart__gridline"
          />
          <text
            x={padL + ((t.ms - tMin) / tSpan) * plotW}
            y={padT + plotH + 14}
            class="hv-leaf-chart__axis-label"
            text-anchor="middle"
          >
            {t.label}
          </text>
        </g>
      ))}

      {/* Plot frame */}
      <rect x={padL} y={padT} width={plotW} height={plotH} class="hv-leaf-chart__frame" />

      {/* The data line */}
      <path d={d.trim()} class="hv-leaf-chart__line" />
    </svg>
  );
}

/** Return ~n "nice" tick values covering [lo, hi]. Uses the standard
 *  1/2/5 × 10^k step selection so labels are round numbers. */
function niceTicks(lo: number, hi: number, target: number): number[] {
  const rawStep = (hi - lo) / target;
  const mag = Math.pow(10, Math.floor(Math.log10(rawStep)));
  const norm = rawStep / mag;
  const step = (norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10) * mag;
  const first = Math.ceil(lo / step) * step;
  const out: number[] = [];
  for (let v = first; v <= hi + step * 0.001; v += step) out.push(Number(v.toFixed(10)));
  return out;
}

/** Time-axis ticks. Format depends on the window span. */
function niceTimeTicks(tMin: number, tMax: number, count: number): { ms: number; label: string }[] {
  const span = tMax - tMin;
  const step = span / count;
  const out: { ms: number; label: string }[] = [];
  for (let i = 0; i <= count; i++) {
    const ms = tMin + step * i;
    out.push({ ms, label: formatTimeTick(new Date(ms), span) });
  }
  return out;
}

function formatTimeTick(d: Date, spanMs: number): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  if (spanMs <= 60 * 60_000) {
    // ≤ 1h → HH:mm:ss
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
  }
  if (spanMs <= 24 * 60 * 60_000) {
    // ≤ 24h → HH:mm
    return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  }
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
